import React, { useState, useEffect } from 'react';
import { Redirect, Route, RouterProps, Switch } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import Account from './Account';
import AdminPanel from './admin';
import Aermod3D from './aermod3d';
import AiAir from './ai-air';
import AiEarth from './ai-earth';
import Benchmarking from './Benchmarking';
import CorrectiveTracker from './CorrectiveTracker';
import CeresHome from './home';
import MyEnviroAi from './my-enviro-ai';
import Checkout from './checkout';
import Generator from './Generator';
import Header from '../components/Header';
import { IAuthContext } from '../context/auth/auth-context';
import MomentUtils from '@date-io/moment';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import MyTundra from './myTundra';
import NewProject from './new-project';
import NotificationPage from './Notification';
import PeopleFinderPage from './people-finder/index';
import Sentiment from './sentiment/index';
import NotificationProvider from '../context/notifications/NotificationProvider';
import Page404 from './404';
import PageTermsConditions from './TermsConditions';
import PageTutorial from './Tutorial';
import PlansAndPricing from './PlansAndPricing';
import ProjectProvider from '../context/project/ProjectProvider';
import ProjectSwitcher from '../components/projects/ProjectSwitcher';
import ProtectedRoute from '../ProtectedRoute';
import Reports from './reporttour';
import Sidebar from '../components/Sidebar';
// import SidebarOverlay from '../components/SidebarOverlay';
import StorePricing from './store/index';
import Survey from './survey/Index';
import { TIERS } from '../types/tiers';
import TrialRequest from './TrialRequest';
import UserProvider from '../context/user/UserProvider';
import ValidatorPage from './Validator';
import { projectId } from '../services/firebase';
import styled from 'styled-components';
import withAuthContext from '../context/auth/AuthConsumer';
import { ISearch } from '../store/aiearth/types';
import { searchFavorite } from '../services/firebase';
import mountainHeroBg from '../assets/mountain-6.jpg';
import {
  updateAIEarth
} from '../store/aiearth/action';
import { IAIEarth } from '../store/aiearth/types';
import { mapFavoritesPayload } from '../util/favoriteApi.util';
import EnviroDeepResearch from './enviro-deep-research';
import EnviroChat from './enviro-chat';
import PermitAgent from './permit-agent';
import EnviroChemicalAI from './enviro-chemical-ai';
import NewChemicalReview from './enviro-chemical-ai/new-chemical-review';

const ShellContainer = styled.div<{ page: string; $embedded?: boolean }>`
  min-height: 100vh;
  background-color: ${(props) =>
    props.$embedded
      ? '#ffffff'
      : props.page === '/myenviroai'
        ? '#02070d'
        : props.page === '/my-tundra'
          ? '#000'
          : props.page === '/search' ||
            props.page === '/enviro-chat' ||
            props.page.startsWith('/enviro-chat/') ||
            props.page === '/people-finder' ||
            props.page === '/sentiment' ||
            props.page === '/trackbot' ||
            props.page === '/benchmarking' ||
            props.page === '/corrective-tracker' ||
            props.page === '/validator' ||
            props.page === '/generator' ||
            props.page.startsWith('/admin')
            ? 'transparent'
            : '#fff'};
  ${(props) =>
    props.$embedded
      ? ''
      : props.page === '/search' ||
        props.page === '/enviro-chat' ||
        props.page.startsWith('/enviro-chat/') ||
        props.page === '/people-finder' ||
        props.page === '/sentiment' ||
        props.page === '/trackbot' ||
        props.page === '/benchmarking' ||
        props.page === '/corrective-tracker' ||
        props.page === '/validator' ||
        props.page === '/generator' ||
        props.page.startsWith('/admin')
        ? `
    background-image: linear-gradient(180deg, rgba(0, 0, 0, 0.52) 0%, rgba(0, 0, 0, 0.66) 48%, rgba(0, 0, 0, 0.75) 100%), url(${mountainHeroBg});
    background-position: center 32%;
    background-size: cover;
    background-repeat: no-repeat;
    background-attachment: fixed;
  `
        : ''}
`;
/** Must match Sidebar `max-width` so main content clears the open sidebar (sidebar is `position: fixed`). */
const SIDEBAR_OPEN_INSET_PX = 252;

const MainContainer = styled.div<{ issearch: string }>`
  padding-top: ${(props) => (props.issearch ? '0' : '0')};
  box-sizing: border-box;
`;
const CenterContainer = styled.div<{ page: string; issearch: string; $sidebarOpen: boolean }>`
  margin-left: ${(p) => (p.$sidebarOpen ? `${SIDEBAR_OPEN_INSET_PX}px` : '0')};
  transition: margin-left 0.3s ease;
  padding:  ${(props) =>
    props.page === '/search' && props.issearch ? '7% 0 0' : '0'};
  height: ${(props) => (props.page === '/admin/validatorUsage' || props.page === '/admin/users') ? '100vh' : 'auto'};
  overflow: ${(props) => (props.page === '/admin/validatorUsage' || props.page === '/admin/users') ? 'auto' : 'none'};;
  ${(props) => props.page === '/my-tundra' || props.page === '/ai-earth' || props.page === '/ai-earth-air' || props.page === '/aermod3d' ? `
    height: 100vh;
    overflow: hidden;
  ` : ''}
`;
const Shell: React.FC<RouterProps & { context: IAuthContext }> = (shellProps) => {
  const context = shellProps.context as IAuthContext;
  const [isSidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isProjectSwitcherVisible, showProjectSwitcher] = useState(false);
  const [getSearchData, setSearchData] = useState<any>('');
  const searchurl = '/search' + (getSearchData ? '/' + getSearchData : getSearchData);
  const store: any = useSelector<any>((state): any => state);
  const searchState: ISearch = store.search;
  const aiEarthState: IAIEarth = store.aiearth;
  const dispatch = useDispatch();

  useEffect(() => {
    if (!context.isAuthenticated || !context.isInitialized) {
      return;
    }

    const path = shellProps.history.location.pathname;
    if (path === '/enviro-chat' || path.startsWith('/enviro-chat/')) {
      return;
    }

    const favoritesFn = async (requestBody: any) => {
      try {
        const result: any = await searchFavorite(requestBody);
        const { list } = mapFavoritesPayload(result?.data, requestBody.previewOnly === true);
        dispatch(updateAIEarth({
          ...aiEarthState,
          favorites: list,
          favoritesLoaded: true,
        }));
      } catch (error) {
        dispatch(updateAIEarth({
          ...aiEarthState,
          favorites: [],
          favoritesLoaded: true,
        }));
        // tslint:disable-next-line:no-console
        console.log('search fav error', error);
      }
    };

    favoritesFn({ type: 'getAll', pageSize: 15, previewOnly: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.isAuthenticated, context.isInitialized, shellProps.history.location.pathname]);

  // Collapse sidebar on search
  useEffect(() => {
    if ((!!shellProps.history.location.search && shellProps.history.location.pathname === '/enviro-chat') || shellProps.history.location.pathname !== '/enviro-chat') {
      setSidebarCollapsed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shellProps.history.location.search, shellProps.history.location.pathname]);

  useEffect(() => {
    const openMainSidebar = () => setSidebarCollapsed(false);
    window.addEventListener('ceres-open-main-sidebar', openMainSidebar);
    return () => window.removeEventListener('ceres-open-main-sidebar', openMainSidebar);
  }, []);

  const isEmbeddedFrame = typeof window !== 'undefined' && window.top !== window.self;
  const showSidebar =
    !isEmbeddedFrame &&
    shellProps.history.location.pathname !== '/permit-agent' &&
    shellProps.history.location.pathname !== '/enviro-chemical-ai' &&
    !shellProps.history.location.pathname.startsWith('/enviro-chemical-ai/');
  const sidebarOpen = showSidebar && !isSidebarCollapsed;

  useEffect(() => {
    const className = 'ceres-main-sidebar-open';
    if (sidebarOpen) {
      document.body.classList.add(className);
    } else {
      document.body.classList.remove(className);
    }
    return () => {
      document.body.classList.remove(className);
    };
  }, [sidebarOpen]);

  return (
    <UserProvider>
      <NotificationProvider uid={(context.user && context.user.uid) || ''}>
        <ProjectProvider>
          <MuiPickersUtilsProvider utils={MomentUtils}>
            <ShellContainer page={shellProps.history.location.pathname} $embedded={isEmbeddedFrame}>
              {!isEmbeddedFrame && (
                <Header
                  isHomePage={shellProps.history.location.pathname === '/'}
                  isEnviroChat={shellProps.history.location.pathname === '/myenviroai'}
                  isAIEarth={shellProps.history.location.pathname === '/ai-earth'}
                  isAIEarthAir={shellProps.history.location.pathname === '/ai-earth-air'}
                  isAIAir={shellProps.history.location.pathname === '/ai-air'}
                  isBenchmarking={shellProps.history.location.pathname === '/benchmarking'}
                  isCorrectiveTracker={shellProps.history.location.pathname === '/corrective-tracker'}
                  isGenerator={shellProps.history.location.pathname === '/generator'}
                  isTrackbot={shellProps.history.location.pathname === '/trackbot'}
                  isPeopleFinder={shellProps.history.location.pathname === '/people-finder'}
                  isSentiment={shellProps.history.location.pathname === '/sentiment'}
                  isStore={shellProps.history.location.pathname.includes('/store')}
                  history={shellProps.history}
                  isSearch={shellProps.history.location.pathname === searchurl}
                  getSearchData={getSearchData}
                  setSearchData={setSearchData}
                  onProjectSwitcherClick={() => showProjectSwitcher(true)}
                  onHamburgerClick={() => {
                    setSidebarCollapsed((prevState) => {
                      return !prevState;
                    });
                  }}
                  searchState={searchState}
                />
              )}
              <MainContainer issearch={searchState && searchState.isSearch ? 'yes' : ''}>
                {shellProps.history.location.pathname !== '/permit-agent' &&
                  shellProps.history.location.pathname !== '/enviro-chemical-ai' &&
                  !shellProps.history.location.pathname.startsWith('/enviro-chemical-ai/') && (
                    <Sidebar
                      isCollapsed={isSidebarCollapsed}
                      onItemClick={() => {
                        setSidebarCollapsed(true);
                      }}
                      onLogoutClick={() => shellProps.history.push('/login')}
                      onLoginClick={() => shellProps.history.push('/login')}
                    />
                  )}
                <CenterContainer
                  page={shellProps.history.location.pathname + shellProps.history.location.search}
                  issearch={searchState && searchState.isSearch ? 'yes' : ''}
                  $sidebarOpen={sidebarOpen}
                >
                  <Switch>
                    <Route path="/search/:query?" exact component={CeresHome} />
                    <Route path="/homepage" exact component={CeresHome} />
                    <Route path="/" exact component={() => <Redirect to="/enviro-chat" />} />
                    <Route
                      path="/about"
                      exact
                      component={() => {
                        window.location.href = 'http://about.enviro.ai';
                        return null;
                      }}
                    />
                    <Route
                      path="/contact"
                      exact
                      component={() => {
                        window.location.href = 'https://about.enviro.ai/#ContactUs';
                        return null;
                      }}
                    />
                    <Route path="/tutorial" exact component={PageTutorial} />
                    <Route path="/termsandconditions" exact component={PageTermsConditions} />
                    <ProtectedRoute path="/projects/new" tiers={[TIERS.PLATINUM, TIERS.DAYPASS]} exact component={NewProject} />
                    <ProtectedRoute
                      path="/account"
                      tiers={[TIERS.PLATINUM, TIERS.DAYPASS, TIERS.SUPER_ADMIN, TIERS.FREE, TIERS.BASIC]}
                      exact
                      component={Account}
                    />
                    <ProtectedRoute
                      path="/myenviroai"
                      // tiers={[TIERS.PLATINUM, TIERS.DAYPASS, TIERS.SUPER_ADMIN]}
                      exact
                      component={MyEnviroAi}
                    />
                    <ProtectedRoute
                      path="/permit-agent"
                      // tiers={[TIERS.PLATINUM, TIERS.DAYPASS, TIERS.SUPER_ADMIN]}
                      exact
                      component={PermitAgent}
                    />
                    {projectId === 'ceres-platform-test' && (
                      <ProtectedRoute
                        path="/enviro-chemical-ai"
                        // tiers={[TIERS.PLATINUM, TIERS.DAYPASS, TIERS.SUPER_ADMIN]}
                        exact
                        component={EnviroChemicalAI}
                      />
                    )}
                    {projectId === 'ceres-platform-test' && (
                      <ProtectedRoute
                        path="/enviro-chemical-ai/new-chemical-review"
                        // tiers={[TIERS.PLATINUM, TIERS.DAYPASS, TIERS.SUPER_ADMIN]}
                        exact
                        component={NewChemicalReview}
                      />
                    )}
                    <Route path="/enviro-chat" exact component={EnviroChat} />
                    <Route path="/enviro-chat/:id" exact component={EnviroChat} />
                    <ProtectedRoute
                      path={['/enviro-deep-research', '/enviro-deep-research/:chatId']}
                      // tiers={[TIERS.PLATINUM, TIERS.DAYPASS, TIERS.SUPER_ADMIN]}
                      exact
                      component={EnviroDeepResearch}
                    />
                    {/* <ProtectedRoute
                      path="/phase1-agent"
                      tiers={[TIERS.PLATINUM, TIERS.DAYPASS, TIERS.SUPER_ADMIN]}
                      exact
                      component={Phase1Agent}
                    /> */}
                    <ProtectedRoute
                      path="/ai-earth"
                      tiers={[TIERS.PLATINUM, TIERS.DAYPASS, TIERS.SUPER_ADMIN]}
                      exact
                      component={AiEarth}
                    />
                    <ProtectedRoute
                      path="/ai-earth-air"
                      tiers={[TIERS.PLATINUM, TIERS.DAYPASS, TIERS.SUPER_ADMIN]}
                      exact
                      component={AiEarth}
                    />
                    {projectId === 'ceres-platform-test' && (
                      <ProtectedRoute
                        path="/ai-air"
                        tiers={[TIERS.PLATINUM, TIERS.DAYPASS, TIERS.SUPER_ADMIN]}
                        exact
                        component={AiAir}
                      />
                    )}
                    <ProtectedRoute
                      path="/trackbot"
                      tiers={[TIERS.PLATINUM, TIERS.DAYPASS, TIERS.SUPER_ADMIN]}
                      exact
                      component={NotificationPage}
                    />
                    <ProtectedRoute
                      path="/people-finder"
                      tiers={[TIERS.PLATINUM, TIERS.DAYPASS, TIERS.SUPER_ADMIN]}
                      exact
                      component={PeopleFinderPage}
                    />
                    <ProtectedRoute
                      path="/sentiment"
                      tiers={[TIERS.PLATINUM, TIERS.DAYPASS, TIERS.SUPER_ADMIN]}
                      exact
                      component={Sentiment}
                    />
                    <ProtectedRoute
                      path="/validator"
                      tiers={[TIERS.PLATINUM, TIERS.DAYPASS, TIERS.SUPER_ADMIN]}
                      exact
                      component={ValidatorPage}
                    />

                    <ProtectedRoute
                      path="/benchmarking"
                      tiers={[TIERS.PLATINUM, TIERS.DAYPASS, TIERS.SUPER_ADMIN]}
                      exact
                      component={Benchmarking}
                    />
                    <ProtectedRoute
                      path="/corrective-tracker"
                      tiers={[TIERS.PLATINUM, TIERS.DAYPASS, TIERS.SUPER_ADMIN]}
                      exact
                      component={CorrectiveTracker}
                    />
                    <ProtectedRoute
                      path="/generator"
                      tiers={[TIERS.PLATINUM, TIERS.DAYPASS, TIERS.SUPER_ADMIN]}
                      exact
                      component={Generator}
                    />
                    <ProtectedRoute
                      path="/aermod3d"
                      tiers={[TIERS.PLATINUM, TIERS.DAYPASS, TIERS.SUPER_ADMIN]}
                      exact
                      component={Aermod3D}
                    />
                    <ProtectedRoute path="/checkout" component={Checkout} />
                    <ProtectedRoute path="/admin" tiers={[TIERS.SUPER_ADMIN]} component={AdminPanel} />
                    <Route path="/plans-and-pricing" component={PlansAndPricing} />
                    <Route path="/store" component={StorePricing} />
                    <Route path="/store/:page" component={StorePricing} />
                    <Route path="/trial-request" component={TrialRequest} />
                    <Route path="/survey" component={Survey} />
                    <Route path="/my-tundra" component={MyTundra} />
                    <ProtectedRoute
                      path="/reporttour"
                      tiers={[TIERS.PLATINUM, TIERS.DAYPASS, TIERS.SUPER_ADMIN]}
                      exact
                      component={Reports}
                    />
                    <Route component={Page404} />
                  </Switch>
                </CenterContainer>
                {/* <SidebarOverlay
                  onClick={() => setSidebarCollapsed((prevState) => !prevState)}
                  isActive={!isSidebarCollapsed}
                /> */}
              </MainContainer>
            </ShellContainer>
            <ProjectSwitcher
              show={isProjectSwitcherVisible}
              onNewProjectClick={() => {
                showProjectSwitcher(false);
                return shellProps.history.push('/projects/new');
              }}
              handleClose={() => showProjectSwitcher(false)}
            />
          </MuiPickersUtilsProvider>
        </ProjectProvider>
      </NotificationProvider>
    </UserProvider>
  );
};

export default withAuthContext(Shell);
